/**
 * Amazon Ads OAuth callback page (popup mode) — pure relay.
 *
 * Amazon LWA redirects here after the user authorizes. This page:
 * 1. Extracts code & state from the URL
 * 2. Calls POST /auth/amazon/callback to exchange the code
 * 3. Sends the full response data back to the opener via postMessage
 * 4. Closes itself
 *
 * All UI logic (profile selection, sync progress) lives on the login page.
 */
import { IconBrandAmazon } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { i18n } from "@/i18n";
import { notifyAmazonAdsOpener } from "@/lib/amazon-ads-oauth";
import { exchangeAmazonCode } from "@/lib/amazon-api";
import { getApiErrorMessage } from "@/lib/api-error";

export const Route = createFileRoute("/auth/amazon/ads-callback")({
  component: AdsCallbackPage,
});

function AdsCallbackPage() {
  const { t } = useTranslation("auth");
  const [state, setState] = useState<"loading" | "success" | "error">("loading");
  const [error, setError] = useState<string | null>(null);
  const exchangedRef = useRef(false);

  useEffect(() => {
    if (exchangedRef.current) return;
    exchangedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const stateParam = params.get("state");

    if (!code || !stateParam) {
      // 全局 i18n 实例：避免把 t 加进 effect 依赖（exchange 只能执行一次）
      setError(i18n.t("auth:adsCallback.missingParams"));
      setState("error");
      return;
    }

    exchangeAmazonCode(code, stateParam)
      .then((data) => {
        // Backend always returns { type: "select_profile", profiles, selectionState }
        // Forward everything to the opener so it can render profile selection.
        if (window.opener) {
          notifyAmazonAdsOpener(window.opener, window.location.origin, {
            profiles: data.profiles,
            selectionState: data.selectionState,
            accountEmail: data.accountEmail ?? null,
          });
        }
        setState("success");
        // Small delay so the user sees the success state before the popup closes
        setTimeout(() => window.close(), 300);
      })
      .catch(async (err) => {
        console.error("Amazon Ads callback exchange failed", err);
        setError(await getApiErrorMessage(err));
        setState("error");
      });
  }, []);

  // ─── Error State ────────────────────────────────────────────────
  if (state === "error") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <div className="flex flex-col items-center gap-4 text-center max-w-[280px]">
          <p className="text-destructive-foreground text-sm break-words">{error}</p>
          {window.opener ? (
            <button
              type="button"
              className="text-sm text-muted-foreground underline underline-offset-4"
              onClick={() => window.close()}
            >
              {t("adsCallback.closeWindow")}
            </button>
          ) : (
            <button
              type="button"
              className="text-sm text-muted-foreground underline underline-offset-4"
              onClick={() => {
                window.location.href = "/onboarding";
              }}
            >
              {t("adsCallback.goToOnboarding")}
            </button>
          )}
        </div>
      </div>
    );
  }

  // ─── Loading / Success State ────────────────────────────────────
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="flex flex-col items-center gap-4 text-center">
        <IconBrandAmazon
          className={`size-10 text-muted-foreground ${state === "success" ? "" : "animate-pulse"}`}
        />
        <p className="text-muted-foreground text-sm break-words">
          {state === "success" ? t("adsCallback.connected") : t("adsCallback.connecting")}
        </p>
      </div>
    </div>
  );
}
